import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Empty, Typography, Tag, Spin } from 'antd';
import { allocationActions, reportingActions } from '../../redux/actions';
import {
    findAccountReferences,
    countReferences,
    unwrapAccount,
    formulaReferences,
} from '../../utils/accountStructure/accountSearch';
import './styles.scss';

const { Text } = Typography;

const formatPeriods = (periods) => {
    if (!periods.length) return null;
    if (periods.length === 1) return periods[0];
    const sorted = [...periods].sort();
    return `${sorted[0]} … ${sorted[sorted.length - 1]} (${periods.length} periods)`;
};

// Formula text with the references to `number` emphasised.
const HighlightedFormula = ({ formula, number }) => {
    const parts = formula.split(/(\{[^}]+\})/g);
    return (
        <code className="where-used-formula">
            {parts.map((part, i) =>
                formulaReferences(part, number) ? <mark key={i}>{part}</mark> : <span key={i}>{part}</span>,
            )}
        </code>
    );
};

const AccountLink = ({ account, onLocate }) => {
    const label = (
        <>
            <Text strong>{account.account_number}</Text>
            {account.name && <Text type="secondary"> {account.name}</Text>}
        </>
    );
    if (!onLocate || account.account_id == null) return label;
    return (
        <button type="button" className="where-used-link" onClick={() => onLocate(account)} title="Show in table">
            {label}
        </button>
    );
};

const Section = ({ title, count, children }) =>
    count > 0 ? (
        <div className="where-used-section">
            <div className="where-used-section-title">
                {title} <Tag>{count}</Tag>
            </div>
            {children}
        </div>
    ) : null;

const FormulaList = ({ items, number, onLocate }) =>
    items.map((item) => (
        <div key={item.identity ?? item.account.account_number} className="where-used-item">
            <AccountLink account={item.account} onLocate={onLocate} />
            {item.section && <Text type="secondary" className="where-used-meta"> · {item.section.label}</Text>}
            {item.formulas.map(({ formula, periods }) => (
                <div key={formula} className="where-used-formula-row">
                    <HighlightedFormula formula={formula} number={number} />
                    {periods.length > 0 && <Text type="secondary" className="where-used-meta">{formatPeriods(periods)}</Text>}
                </div>
            ))}
        </div>
    ));

/**
 * Lists everything that references `account`: formulas on other accounts,
 * cell formulas in the open scenario, weighted-average settings, the parent
 * it rolls up into, favorite tabs, allocations and reporting widgets.
 */
const WhereUsed = ({ account, accountStructure, scenario, onLocate }) => {
    const dispatch = useDispatch();
    const allocations = useSelector((state) => state.allocations.allocations);
    const fetchingAllocations = useSelector((state) => state.allocations.fetchingAllocations);
    const widgets = useSelector((state) => state.reporting.widgets);
    const fetchingWidgets = useSelector((state) => state.reporting.fetchingWidgets);
    const businessCases = useSelector((state) => state.business.businessCases);
    const entities = useSelector((state) => state.entities.entities);

    // Allocations and widgets are only loaded by their own pages.
    useEffect(() => {
        if (!fetchingAllocations && !(allocations?.length > 0)) dispatch(allocationActions.getAllocations());
        dispatch(reportingActions.getReportingWidgets());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const refs = useMemo(
        () => findAccountReferences({ structure: accountStructure, target: account, allocations, widgets, scenario }),
        [accountStructure, account, allocations, widgets, scenario],
    );

    const bcNames = useMemo(
        () => new Map((businessCases || []).map((b) => [Number(b.business_case_id), b.name])),
        [businessCases],
    );

    const entityNames = useMemo(() => {
        const m = new Map();
        const walk = (nodes) =>
            (nodes || []).forEach((n) => {
                if (n?.entity_id != null) m.set(Number(n.entity_id), n.name);
                if (Array.isArray(n?.children)) walk(n.children);
            });
        walk(Array.isArray(entities) ? entities : Object.values(entities || {}));
        return m;
    }, [entities]);

    const acc = unwrapAccount(account);
    if (!acc) return null;
    const number = acc.account_number;
    const total = countReferences(refs);
    const loading = fetchingAllocations || fetchingWidgets;

    const entityLabel = (id) => (id == null || Number(id) < 0 ? 'All entities' : entityNames.get(Number(id)) ?? `Entity ${id}`);

    return (
        <div className="where-used">
            <div className="where-used-header">
                <Text strong>{number}</Text>
                {acc.name && <Text type="secondary"> {acc.name}</Text>}
                {loading && <Spin size="small" style={{ marginLeft: 8 }} />}
            </div>

            {total === 0 ? (
                <Empty description={`Nothing references ${number}`} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
                <>
                    <Section title="Formulas in the account structure" count={refs.formulas.length}>
                        <FormulaList items={refs.formulas} number={number} onLocate={onLocate} />
                    </Section>

                    <Section title="Cell formulas in this scenario" count={refs.scenarioFormulas.length}>
                        <FormulaList items={refs.scenarioFormulas} number={number} onLocate={onLocate} />
                    </Section>

                    <Section title="Weighting" count={refs.weights.length}>
                        {refs.weights.map((w) => (
                            <div key={`${w.identity}-${w.kind}`} className="where-used-item">
                                <AccountLink account={w.account} onLocate={onLocate} />
                                <Text type="secondary" className="where-used-meta"> · {w.kind}</Text>
                            </div>
                        ))}
                    </Section>

                    <Section title="Rolls up into" count={refs.parent ? 1 : 0}>
                        {refs.parent && (
                            <div className="where-used-item">
                                <AccountLink account={refs.parent.account} onLocate={onLocate} />
                                <Text type="secondary" className="where-used-meta"> · {refs.parent.section.label}</Text>
                            </div>
                        )}
                    </Section>

                    <Section title="Favorite tabs" count={refs.customTabs.length}>
                        {refs.customTabs.map((t) => (
                            <div key={t.tabKey} className="where-used-item">{t.name}</div>
                        ))}
                    </Section>

                    <Section title="Allocations" count={refs.allocations.length}>
                        {refs.allocations.map(({ allocation: a, role }) => (
                            <div key={a.allocation_id} className="where-used-item">
                                <Tag color={role === 'Target' ? 'blue' : 'gold'}>{role}</Tag>
                                {bcNames.get(Number(a.businessCaseId)) ?? 'No business case'}
                                <Text type="secondary" className="where-used-meta">
                                    {' · '}
                                    {entityLabel(a.entityId)} → {entityLabel(a.targetEntityId)}
                                </Text>
                            </div>
                        ))}
                    </Section>

                    <Section title="Reporting widgets" count={refs.widgets.length}>
                        {refs.widgets.map(({ widget, title, count }) => (
                            <div key={widget.widget_id} className="where-used-item">
                                {title}
                                {count > 1 && <Text type="secondary" className="where-used-meta"> · {count} series</Text>}
                            </div>
                        ))}
                    </Section>
                </>
            )}
        </div>
    );
};

export default WhereUsed;
